import { DatePicker } from 'antd'
import { DatePickerProps as AntDatePickerProps } from 'antd/lib/date-picker/interface'
import moment, { Moment } from 'moment-timezone'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDateTime } from '../../../shared/useDateTime'
import { TimeZoneAndLocale } from '../../../types.custom'
const { MonthPicker } = DatePicker

export interface DatePickerProps extends AntDatePickerProps {
  inputDate: string
  onDateChanged: (outputDate: string | undefined) => void
  timeZoneAndLocale: TimeZoneAndLocale
  dateFormat: 'DMY' | 'MY' | 'Y'
  disabled?: boolean
  disabledDate?: (current: Moment | null) => boolean
  showTime?: boolean
  size?: 'large' | 'default' | 'small'
  allowClear?: boolean
  width?: string
  className?: string
  onBlur?: () => void
  keepTimezoneOffset?: boolean
  extraFooter?: React.ReactNode
  placeholder?: string
}

const PerDatePicker: FC<DatePickerProps> = ({
  inputDate,
  onDateChanged,
  timeZoneAndLocale,
  dateFormat,
  disabled,
  disabledDate,
  showTime,
  size,
  allowClear = false,
  width = '200px',
  className,
  onBlur,
  keepTimezoneOffset = false,
  extraFooter = undefined,
  placeholder,
  ...rest
}) => {
  const { utcDateStringToMoment, utcDateStringToMomentWithoutTimezone, momentToUtcDateString, localesWith12HourTimeFormat } =
    useDateTime(timeZoneAndLocale)
  const [t] = useTranslation()
  const inizializationDate = keepTimezoneOffset ? utcDateStringToMomentWithoutTimezone(inputDate) : utcDateStringToMoment(inputDate)
  const [inputDateAsMoment, setInputDateAsMoment] = useState<Moment | undefined>(inizializationDate)
  useEffect(() => {
    setInputDateAsMoment(inizializationDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputDate])

  const handlePickerChange = (changedDateMoment: Moment | null) => {
    if (changedDateMoment !== null) {
      const newDate = keepTimezoneOffset ? changedDateMoment.toISOString(true) : momentToUtcDateString(changedDateMoment)

      if (newDate) {
        // ONLY EMIT VALID DATES! => Prevent clear
        onDateChanged(newDate)
        setInputDateAsMoment(changedDateMoment)
      }
    } else {
      onDateChanged(undefined)
      setInputDateAsMoment(undefined)
    }
  }

  const showTimePicker = showTime
    ? {
        defaultValue: inputDateAsMoment,
        value: inputDateAsMoment,
        onChange: handlePickerChange,
        format: 'HH:mm',
        locale: { placeholder: timeZoneAndLocale.locale },
        placeholder: timeZoneAndLocale.locale,
        use12Hours: localesWith12HourTimeFormat.includes(timeZoneAndLocale.locale),
      }
    : false

  return (
    <>
      {dateFormat === 'Y' && <>YEAR PICKER NOT IMPLEMENTED</>}
      {dateFormat === 'DMY' && (
        <DatePicker
          className={className}
          value={inputDateAsMoment}
          onChange={handlePickerChange}
          onBlur={onBlur}
          placeholder={placeholder || t('datePicker.placeholder')}
          disabled={disabled}
          allowClear={allowClear}
          disabledDate={disabledDate}
          showTime={showTimePicker}
          format={
            showTime
              ? `${moment.localeData(timeZoneAndLocale.locale).longDateFormat('L')} ${moment
                  .localeData(timeZoneAndLocale.locale)
                  .longDateFormat('LT')}`
              : `${moment.localeData(timeZoneAndLocale.locale).longDateFormat('L')}`
          }
          size={size}
          style={{ width: width }}
          data-e2e-datepicker
          renderExtraFooter={() => extraFooter}
          {...rest}
        />
      )}
      {dateFormat === 'MY' && (
        <MonthPicker
          className={className}
          value={inputDateAsMoment}
          onChange={handlePickerChange}
          size={size}
          placeholder={placeholder || t('monthPicker.placeholder')}
          disabled={disabled}
          disabledDate={disabledDate}
          allowClear={allowClear}
          style={{ width: width }}
          data-e2e-monthpicker
          renderExtraFooter={() => extraFooter}
          {...rest}
        />
      )}
    </>
  )
}

export default PerDatePicker
